/* Settings-bar controls for the 3D network view.
 *
 * Two controls: which layout, and what mark size stands for. That is the whole settings surface on
 * purpose.
 *
 * The package exposes several other dials - node scale, edge width and opacity, bundle strength,
 * plane separation - and none of them is here. They are tuning parameters, not reader decisions:
 * each has one value that is right for the data, the view already derives its camera fit and its
 * bundle opacity from the layout, and a settings bar full of sliders would ask a reader to solve a
 * problem the view should have solved. They stay exposed in the package's Storybook playground,
 * which is where a value gets chosen, and arrive here as defaults.
 *
 * Size is the exception to all of that, and not a dial. A layout spends position on the grouping
 * and colour on the component type, which leaves size as the only channel a reader can point at a
 * quantity of their own - so what it stands for is a decision the view cannot make for them. */

import { noop } from 'lodash';
import { ViewIds } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  SettingsType,
  type ButtonConfig,
  type SettingsConfig,
  type ToggleConfig,
} from '@ardoq/view-settings';
import type { NetworkLayoutKind } from '@ardoq/xyz-charts';
import { getRedactedFieldDisplay } from '@ardoq/renderers';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import {
  storeCollapsedGroupChange,
  toCollapseAll,
  toExpandAll,
} from './collapsedGroups';
import type { Network3DAppViewSettings } from './types';
import { ConnectedNetwork3DSearchField } from './Network3DSearchField';
import type { Network3DNodeSizeBy } from './sourceData/toNodeSizing';

/**
 * The four layouts, in the order a reader should meet them.
 *
 * Force first because it is the one that needs no explanation and the one that answers the
 * commonest question - what is connected to what. The two structural layouts follow, and the
 * bundled sphere last: it is the most powerful on a dense estate and the least obvious, since it
 * trades the individual edge for the shape of the coupling.
 */
const LAYOUT_ORDER: NetworkLayoutKind[] = [
  'force',
  'layered',
  'cone-tree',
  'bundled-sphere',
];

/**
 * Labels resolved through a switch rather than by interpolating `option.i18nKey`.
 *
 * An interpolated key is invisible to the extractor, so the strings would never reach `de.json` or
 * `fr.json` and `check-i18n` would not notice they were missing. Same reason `ChartEmptyState` has
 * its callers word their own copy.
 */
const toLayoutLabel = (
  kind: NetworkLayoutKind,
  i18nLocale: I18nLocale
): string => {
  switch (kind) {
    case 'force':
      return t(
        i18nLocale,
        'views:network3d.menu.layout.force',
        'Force-directed'
      );
    case 'layered':
      return t(
        i18nLocale,
        'views:network3d.menu.layout.layered',
        'Layered by type'
      );
    case 'cone-tree':
      return t(
        i18nLocale,
        'views:network3d.menu.layout.coneTree',
        'Radial tree'
      );
    case 'grouped':
      return t(i18nLocale, 'views:network3d.menu.layout.grouped', 'Grouped');
    case 'bundled-sphere':
      return t(
        i18nLocale,
        'views:network3d.menu.layout.bundledSphere',
        'Bundled sphere'
      );
  }
};

/**
 * The size picker's two fixed entries, above whatever fields are in scope.
 *
 * Hand-rolled options rather than `getFieldDropdownOptions`, which the treemap and the isometric
 * block diagram use. That helper writes one settings key and toggles the active option back to
 * `''`, which is exactly right for a control whose only states are "a field" and "off". This one
 * has three kinds of state, so a field click writes two keys - and clicking the active field again
 * has to leave it alone rather than silently fall back to the reference count.
 *
 * What it does borrow is the confidential handling: such a field is offered and disabled rather
 * than hidden, so a reader can see that the field exists and that they cannot read it.
 */
const toSizeLabel = (
  nodeSizeBy: Network3DNodeSizeBy,
  sizeByFieldName: string,
  numericFields: readonly FieldWithPermissions[],
  i18nLocale: I18nLocale
): string => {
  if (nodeSizeBy === 'uniform') {
    return t(i18nLocale, 'views:network3d.menu.size.uniform', 'Uniform');
  }
  if (nodeSizeBy === 'references') {
    return t(i18nLocale, 'views:network3d.menu.size.references', 'References');
  }
  const field = numericFields.find(({ name }) => name === sizeByFieldName);
  /* The reference count, because that is what `toNodeSizing` falls back to when the named field is
     gone or unreadable. A button naming a field the picture is not using is the failure here. */
  return field && !field.isConfidential
    ? field.label || field.name
    : t(i18nLocale, 'views:network3d.menu.size.references', 'References');
};

export type Network3DViewModifiersArgs = {
  viewSettings: Network3DAppViewSettings;
  numericFields: FieldWithPermissions[];
  /** Whether a grouping rule is arranging the view - see `layoutDropdown`. */
  isGrouped: boolean;
  i18nLocale: I18nLocale;
};

/**
 * Whether marks and regions are named on the canvas.
 *
 * A reader decision rather than a tuning dial, which is why it is in the bar at all: a dense
 * estate is sometimes read for its shape and the text is then in the way, and the same estate is
 * sometimes read for who is in it. What cannot be a reader decision is *which* of several hundred
 * marks get named at a given camera - `placeNetworkLabels` answers that, and a dropdown offering
 * it would be asking the reader to solve the view's problem.
 */
const labelsToggle = (
  viewSettings: Network3DAppViewSettings,
  i18nLocale: I18nLocale
): ToggleConfig => ({
  id: 'network3dShowLabels',
  type: SettingsType.TOGGLE,
  label: t(i18nLocale, 'views:network3d.menu.labels', 'Labels'),
  iconName: IconName.TEXT_FORMAT,
  isActive: viewSettings.showLabels,
  currentValue: viewSettings.showLabels,
  onViewSettingsUpdate: () =>
    handleStoreSetting(ViewIds.NETWORK_3D, {
      showLabels: !viewSettings.showLabels,
    }),
});

/**
 * The layout picker, which says what is on screen rather than what was last chosen.
 *
 * A grouping rule overrides the setting: `drawnSettings` rewrites `layoutKind` to `grouped`,
 * because only that layout can express a grouping and the other four would draw a grouped graph
 * as the same cloud with some invisible nodes in it. The control used to name the stored choice
 * through all of that, so it read `Layered by type` over a picture that was plainly grouped, and
 * picking a different one appeared to do nothing.
 *
 * So while a rule is on, `Grouped` is offered as the selected option and the four arrangements
 * are offered disabled, under a heading that says once why. Selected rather than only named in
 * the button, because the settings panel renders the *active option* rather than `buttonText` -
 * setting one without the other left the panel still reading `Layered by type` over a grouped
 * picture, which is the half of the complaint that is most visible.
 *
 * The stored choice is not ticked while it is overridden. It is recoverable - removing the rule
 * brings it back - and only one option can read as selected, so the choice is between naming what
 * is drawn and naming what would be. Naming what is drawn is the whole point here.
 */
const layoutDropdown = (
  viewSettings: Network3DAppViewSettings,
  isGrouped: boolean,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'network3dLayout',
  type: SettingsType.DROPDOWN,
  iconName: IconName.ACCOUNT_TREE,
  label: t(i18nLocale, 'views:network3d.menu.layout.label', 'Layout'),
  buttonText: toLayoutLabel(
    isGrouped ? 'grouped' : viewSettings.layoutKind,
    i18nLocale
  ),
  options: [
    ...(isGrouped
      ? [
          {
            name: 'network3dLayout_groupingDrives',
            label: t(
              i18nLocale,
              'views:network3d.menu.layout.groupingDrives',
              'Groups are arranging this view'
            ),
            type: DropdownOptionType.HEADER,
          },
          {
            name: 'network3dLayout_grouped',
            label: toLayoutLabel('grouped', i18nLocale),
            type: DropdownOptionType.OPTION,
            isActive: true,
            /* Not a choice: it follows from the grouping rule, and removing that rule is how a
               reader leaves it. Offered at all so the control has something true to name. */
            isDisabled: true,
            onClick: noop,
          },
        ]
      : []),
    ...LAYOUT_ORDER.map(kind => ({
      name: `network3dLayout_${kind}`,
      label: toLayoutLabel(kind, i18nLocale),
      type: DropdownOptionType.OPTION,
      isActive: !isGrouped && viewSettings.layoutKind === kind,
      isDisabled: isGrouped,
      onClick: () =>
        handleStoreSetting(ViewIds.NETWORK_3D, { layoutKind: kind }),
    })),
  ],
  dropdownSize: DropdownSize.S,
});

/**
 * Find a component, which is the one question a picture of several hundred marks cannot be read
 * for. A field rather than a dropdown, because the answer is not on a list the view can offer.
 *
 * Its own state rather than a view setting: settings are stashed into the user profile
 * continuously, and a query that outlived the session would reopen the view tomorrow with most of
 * the estate dimmed and nothing saying why. See `network3dSearch$`.
 */
const searchField = (i18nLocale: I18nLocale): SettingsConfig => ({
  id: 'network3dSearch',
  type: SettingsType.CUSTOM_COMPONENT,
  label: t(i18nLocale, 'views:network3d.menu.search.label', 'Search'),
  iconName: IconName.SEARCH,
  component: ConnectedNetwork3DSearchField,
});

export const getNetwork3DViewModifiers = ({
  viewSettings,
  numericFields,
  isGrouped,
  i18nLocale,
}: Network3DViewModifiersArgs): SettingsConfig[] => [
  searchField(i18nLocale),
  layoutDropdown(viewSettings, isGrouped, i18nLocale),
  labelsToggle(viewSettings, i18nLocale),
  {
    id: 'network3dNodeSize',
    type: SettingsType.DROPDOWN,
    iconName: IconName.CIRCLE,
    label: t(i18nLocale, 'views:network3d.menu.size.label', 'Component size'),
    buttonText: toSizeLabel(
      viewSettings.nodeSizeBy,
      viewSettings.sizeByFieldName,
      numericFields,
      i18nLocale
    ),
    options: [
      {
        name: 'network3dNodeSize_uniform',
        label: t(i18nLocale, 'views:network3d.menu.size.uniform', 'Uniform'),
        type: DropdownOptionType.OPTION,
        isActive: viewSettings.nodeSizeBy === 'uniform',
        onClick: () =>
          handleStoreSetting(ViewIds.NETWORK_3D, { nodeSizeBy: 'uniform' }),
      },
      {
        name: 'network3dNodeSize_references',
        label: t(
          i18nLocale,
          'views:network3d.menu.size.references',
          'References'
        ),
        type: DropdownOptionType.OPTION,
        isActive: viewSettings.nodeSizeBy === 'references',
        onClick: () =>
          handleStoreSetting(ViewIds.NETWORK_3D, { nodeSizeBy: 'references' }),
      },
      /* Only when there is something under it to separate. A heading over nothing reads as a list
         that failed to load rather than as a viewpoint with no numeric fields in it. */
      ...(numericFields.length > 0
        ? [
            {
              name: 'network3dNodeSize_fieldsHeader',
              label: t(
                i18nLocale,
                'views:network3d.menu.size.fields',
                'Fields'
              ),
              type: DropdownOptionType.HEADER,
            },
          ]
        : []),
      ...numericFields.map(({ name, label, isConfidential }) => ({
        name: `network3dNodeSize_field_${name}`,
        label: label || name,
        type: DropdownOptionType.OPTION,
        isActive:
          viewSettings.nodeSizeBy === 'field' &&
          viewSettings.sizeByFieldName === name,
        isDisabled: isConfidential,
        /* The same right-hand marker every other field picker shows, so "you may not read this"
           looks the same wherever a reader meets it. */
        rightContent: isConfidential ? getRedactedFieldDisplay() : undefined,
        /* Both keys at once. Writing only the name would leave `nodeSizeBy` on whatever it was, so
           picking a field while on Uniform would do nothing visible. */
        onClick: () =>
          handleStoreSetting(ViewIds.NETWORK_3D, {
            nodeSizeBy: 'field',
            sizeByFieldName: name,
          }),
      })),
    ],
    dropdownSize: DropdownSize.S,
  },
];

/**
 * Collapse all and Expand all, which write the setting and nothing else.
 *
 * The same pair every grouping view offers, under the same two keys, and here they are the whole
 * of the operation: `buildNetwork3DSourceData` builds the graph with its groups already open or
 * closed, so a stored setting is honoured on load without anything having to re-apply it. No
 * companion action, unlike the flexible block diagram's - see `storeCollapsedGroupIds`.
 *
 * The wildcard is the convention the yFiles views already store for "every group", and storing it
 * rather than the list of ids is what makes Collapse all cover a group the grouping rules have
 * not produced yet.
 */
export const collapseAllButton = (
  viewSettings: Network3DAppViewSettings,
  hasGroups: boolean,
  i18nLocale: I18nLocale
): ButtonConfig => ({
  id: 'collapseAll',
  label: t(i18nLocale, 'appLayout:viewPaneHeader.collapseAll', 'Collapse all'),
  iconName: IconName.COLLAPSE_ALL,
  type: SettingsType.BUTTON,
  isDisabled: !hasGroups,
  isActive: hasGroups && viewSettings.collapsedGroupIds.includes(WILDCARD),
  onClick: () =>
    storeCollapsedGroupChange(toCollapseAll(viewSettings.collapsedGroupIds)),
  popoverText: hasGroups
    ? undefined
    : t(
        i18nLocale,
        'appLayout:viewPaneHeader.noGroupsToCollapse',
        'No groups to collapse'
      ),
});

export const expandAllButton = (
  viewSettings: Network3DAppViewSettings,
  hasGroups: boolean,
  i18nLocale: I18nLocale
): ButtonConfig => ({
  id: 'expandAll',
  label: t(i18nLocale, 'appLayout:viewPaneHeader.expandAll', 'Expand all'),
  iconName: IconName.EXPAND_ALL,
  type: SettingsType.BUTTON,
  isDisabled: !hasGroups,
  isActive: hasGroups && viewSettings.collapsedGroupIds.length === 0,
  onClick: () =>
    storeCollapsedGroupChange(toExpandAll(viewSettings.collapsedGroupIds)),
  popoverText: hasGroups
    ? undefined
    : t(
        i18nLocale,
        'appLayout:viewPaneHeader.noGroupsToExpand',
        'No groups to expand'
      ),
});
